import React from "react";
import styled from "styled-components";

/**
 * Universal Icon Component
 *
 * This component provides a consistent way to use SVG icons throughout the application.
 * It supports both inline SVG components and external SVG files.
 *
 * Usage Examples:
 *
 * 1. With SVG Component:
 *    import { ReactComponent as MyIcon } from '../assets/icons/my-icon.svg';
 *    <Icon component={MyIcon} size="24px" color="#000" />
 *
 * 2. With SVG Path:
 *    import myIcon from '../assets/icons/my-icon.svg';
 *    <Icon src={myIcon} size="24px" />
 *
 * 3. With children (inline SVG):
 *    <Icon size="24px" color="#000">
 *      <svg viewBox="0 0 24 24">...</svg>
 *    </Icon>
 */

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ width, size }) => width || size || "24px"};
  height: ${({ height, size }) => height || size || "24px"};
  color: ${({ color, theme }) =>
    color || theme?.colors?.main?.textMain || "currentColor"};
  transition: all 0.2s ease;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;

    path,
    circle,
    rect,
    polygon,
    line,
    polyline {
      ${({ fillPaths }) => (fillPaths ? "fill: currentColor;" : "")}
      ${({ strokePaths }) => (strokePaths ? "stroke: currentColor;" : "")}
      color: ${({ color }) => color};
      fill: ${({ color }) => color};
    }
  }

  ${({ clickable }) =>
    clickable &&
    `
    cursor: pointer;
    
    &:hover {
      opacity: 0.7;
      transform: scale(1.05);
    }
  `}

  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  `}

  @media screen and (max-width: 576px) {
    width: ${({ mobileWidth, mobileSize }) =>
      mobileWidth || mobileSize || "16px"};
    height: ${({ mobileHeight, mobileSize }) =>
      mobileHeight || mobileSize || "16px"};
  }
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const Icon = ({
  component: Component,
  src,
  size = "24px",
  width,
  height,
  color,
  alt = "icon",
  fillPaths = false,
  strokePaths = false,
  clickable = false,
  disabled = false,
  onClick,
  className,
  children,
  ...props
}) => {
  const handleClick = (e) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  return (
    <IconWrapper
      size={size}
      width={width}
      height={height}
      color={color}
      fillPaths={fillPaths}
      strokePaths={strokePaths}
      clickable={clickable || !!onClick}
      disabled={disabled}
      onClick={handleClick}
      className={className}
      {...props}
    >
      {Component ? (
        <Component />
      ) : src ? (
        <StyledImg src={src} alt={alt} />
      ) : children ? (
        children
      ) : null}
    </IconWrapper>
  );
};

export default Icon;
